import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  List,
  Space,
  Button,
  Typography,
  Empty,
  Spin,
  message,
  Modal,
  Tag,
  Tooltip,
  Collapse,
} from 'antd';
import {
  FileOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { get, del } from '../../../../utils/request';
import FileUploader from './FileUploader';
import FilePreview from '../shared/FilePreview';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

interface Attachment {
  id: number;
  filename: string;
  original_filename: string;
  file_path: string;
  file_url: string;
  file_size: number;
  content_type?: string;
  target_type: string;
  target_id: number;
  category?: string;
  uploaded_by: number;
  uploader_name?: string;
  created_at: string;
  updated_at?: string;
}

interface CaseAttachmentTabProps {
  caseId: number;
  canEdit: boolean;
}

// Category labels
const CATEGORY_LABELS: Record<string, string> = {
  contract: '合同文件',
  evidence: '证据材料',
  legal_doc: '法律文书',
  other: '其他文件',
};

// Category colors
const CATEGORY_COLORS: Record<string, string> = {
  contract: 'blue',
  evidence: 'green',
  legal_doc: 'orange',
  other: 'default',
};

const CaseAttachmentTab: React.FC<CaseAttachmentTabProps> = ({
  caseId,
  canEdit,
}) => {
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [uploaderVisible, setUploaderVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<Attachment | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<number>>(new Set());

  // Load attachments
  const loadAttachments = async () => {
    if (!caseId) return;

    setLoading(true);
    try {
      const data = await get<Attachment[]>('/attachments/', {
        params: {
          target_type: 'case',
          target_id: caseId,
          limit: 1000,
        },
      });
      setAttachments(data || []);
    } catch (error: any) {
      console.error('Failed to load attachments:', error);
      message.error('加载附件失败');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (caseId) {
      loadAttachments();
    }
  }, [caseId]);

  // Filter attachments by category
  const getFilteredAttachments = (): Attachment[] => {
    if (activeCategory === 'all') {
      return attachments;
    }
    return attachments.filter((att) => att.category === activeCategory);
  };

  // Group attachments by category
  const getAttachmentsByCategory = (category: string): Attachment[] => {
    return attachments.filter((att) => att.category === category);
  };

  // Get file icon
  const getFileIcon = (filename: string, contentType?: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    
    if (contentType?.includes('pdf') || extension === 'pdf') {
      return <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />;
    }
    if (
      contentType?.includes('word') ||
      ['doc', 'docx'].includes(extension || '')
    ) {
      return <FileWordOutlined style={{ fontSize: 24, color: '#1890ff' }} />;
    }
    if (
      contentType?.includes('excel') ||
      contentType?.includes('spreadsheet') ||
      ['xls', 'xlsx'].includes(extension || '')
    ) {
      return <FileExcelOutlined style={{ fontSize: 24, color: '#52c41a' }} />;
    }
    if (
      contentType?.includes('image') ||
      ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')
    ) {
      return <FileImageOutlined style={{ fontSize: 24, color: '#722ed1' }} />;
    }
    if (
      contentType?.includes('zip') ||
      ['zip', 'rar', '7z'].includes(extension || '')
    ) {
      return <FileZipOutlined style={{ fontSize: 24, color: '#faad14' }} />;
    }
    if (contentType?.includes('text') || extension === 'txt') {
      return <FileTextOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />;
    }
    
    return <FileOutlined style={{ fontSize: 24, color: '#8c8c8c' }} />;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle preview
  const handlePreview = async (attachment: Attachment) => {
    try {
      // Get download URL
      const response = await get<{ download_url: string }>(
        `/attachments/${attachment.id}/download`
      );
      
      setPreviewFile({
        ...attachment,
        file_url: response.download_url,
      });
      setPreviewVisible(true);
    } catch (error: any) {
      message.error('获取文件预览失败');
    }
  };

  // Handle download
  const handleDownload = async (attachment: Attachment) => {
    setDownloadingIds((prev) => new Set(prev).add(attachment.id));
    
    try {
      // Get download URL
      const response = await get<{ download_url: string; filename: string }>(
        `/attachments/${attachment.id}/download`
      );
      
      // Open download URL in new tab
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = response.filename || attachment.original_filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('开始下载');
    } catch (error: any) {
      message.error('下载失败');
    } finally {
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  // Handle delete
  const handleDelete = (attachment: Attachment) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除文件"${attachment.original_filename}"吗？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await del(`/attachments/${attachment.id}`);
          message.success('删除成功');
          loadAttachments();
        } catch (error: any) {
          message.error('删除失败');
        }
      },
    });
  };

  // Handle upload success
  const handleUploadSuccess = () => {
    setUploaderVisible(false);
    loadAttachments();
  };

  // Render attachment list
  const renderAttachmentList = (attachmentList: Attachment[]) => {
    if (attachmentList.length === 0) {
      return (
        <Empty
          description="暂无附件"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <List
        dataSource={attachmentList}
        renderItem={(attachment) => (
          <List.Item
            key={attachment.id}
            actions={[
              <Tooltip title="预览">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreview(attachment)}
                />
              </Tooltip>,
              <Tooltip title="下载">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  loading={downloadingIds.has(attachment.id)}
                  onClick={() => handleDownload(attachment)}
                />
              </Tooltip>,
              canEdit && (
                <Tooltip title="删除">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(attachment)}
                  />
                </Tooltip>
              ),
            ].filter(Boolean)}
          >
            <List.Item.Meta
              avatar={getFileIcon(
                attachment.original_filename,
                attachment.content_type
              )}
              title={
                <Space>
                  <Text strong>{attachment.original_filename}</Text>
                  {attachment.category && (
                    <Tag color={CATEGORY_COLORS[attachment.category]}>
                      {CATEGORY_LABELS[attachment.category]}
                    </Tag>
                  )}
                </Space>
              }
              description={
                <Space split="|" size="small">
                  <Text type="secondary">
                    {formatFileSize(attachment.file_size)}
                  </Text>
                  <Text type="secondary">
                    {attachment.uploader_name || '未知用户'}
                  </Text>
                  <Text type="secondary">
                    {dayjs(attachment.created_at).format('YYYY-MM-DD HH:mm')}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    );
  };

  // Get category counts
  const getCategoryCounts = () => {
    const counts: Record<string, number> = {
      all: attachments.length,
      contract: 0,
      evidence: 0,
      legal_doc: 0,
      other: 0,
    };

    attachments.forEach((att) => {
      if (att.category && counts[att.category] !== undefined) {
        counts[att.category]++;
      }
    });

    return counts;
  };

  const categoryCounts = getCategoryCounts();

  if (loading && attachments.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div className="case-attachment-tab">
      <Card>
        {/* Header with Upload Button */}
        <div
          style={{
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Text strong style={{ fontSize: 16 }}>
              附件管理
            </Text>
            {attachments.length > 0 && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({attachments.length})
              </Text>
            )}
          </div>
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setUploaderVisible(true)}
            >
              上传附件
            </Button>
          )}
        </div>

        {/* Tabs by Category */}
        <Tabs
          activeKey={activeCategory}
          onChange={setActiveCategory}
          type="card"
        >
          <TabPane
            tab={`全部 (${categoryCounts.all})`}
            key="all"
          >
            {renderAttachmentList(getFilteredAttachments())}
          </TabPane>
          <TabPane
            tab={`合同文件 (${categoryCounts.contract})`}
            key="contract"
          >
            {renderAttachmentList(getAttachmentsByCategory('contract'))}
          </TabPane>
          <TabPane
            tab={`证据材料 (${categoryCounts.evidence})`}
            key="evidence"
          >
            {renderAttachmentList(getAttachmentsByCategory('evidence'))}
          </TabPane>
          <TabPane
            tab={`法律文书 (${categoryCounts.legal_doc})`}
            key="legal_doc"
          >
            {renderAttachmentList(getAttachmentsByCategory('legal_doc'))}
          </TabPane>
          <TabPane
            tab={`其他文件 (${categoryCounts.other})`}
            key="other"
          >
            {renderAttachmentList(getAttachmentsByCategory('other'))}
          </TabPane>
        </Tabs>
      </Card>

      {/* Upload Modal */}
      <Modal
        title="上传附件"
        open={uploaderVisible}
        onCancel={() => setUploaderVisible(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <FileUploader
          caseId={caseId}
          onUploadSuccess={handleUploadSuccess}
        />
      </Modal>

      {/* Preview Modal */}
      {previewFile && (
        <FilePreview
          visible={previewVisible}
          fileUrl={previewFile.file_url}
          fileName={previewFile.original_filename}
          fileType={previewFile.content_type}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewFile(null);
          }}
          onDownload={() => handleDownload(previewFile)}
        />
      )}
    </div>
  );
};

export default CaseAttachmentTab;
